import React from 'react'

import { format, getMonth, getYear } from 'date-fns'
import { hu as dateLocaleHu } from 'date-fns/locale'
import __range from 'lodash/range'
import type { DatePickerProps } from 'react-datepicker'
import DatePicker from 'react-datepicker'
import styled from 'styled-components'

import type { Locale } from '@libs/typescript-utils'

import { CALENDAR_LOCALE_FORMATS } from '@constants'

import { IconButton } from '../Buttons'
import { KeyboardArrowLeftIcon, KeyboardArrowRightIcon } from '../svgIcons'
import type { DatePickerDropdownProps } from './DatePickerDropdown'
import { DatePickerDropdown } from './DatePickerDropdown'

const CustomHeader = styled.div`
  min-width: 252px;
  padding: 0 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${IconButton} {
    :first-of-type {
      order: 1;
    }
    :last-of-type {
      order: 4;
    }
  }
`

const StyledDatePickerDropdown: React.ComponentType<DatePickerDropdownProps<number>> = styled(DatePickerDropdown)`
  max-width: 120px;
  flex: 1;
`

type CustomDatePickerProps = DatePickerProps & {
  hideMonthSelect?: boolean
}

export function CustomDatePicker({
  className,
  hideMonthSelect = false,
  locale = 'hu',
  minDate,
  maxDate,
  selected,
  ...props
}: CustomDatePickerProps) {
  // Note: year limitation -20 to +5 years of current year
  const yearsOptions = React.useMemo(() => {
    const currentYear = getYear(new Date())
    const selectedYear = selected ? getYear(selected) : currentYear
    const minYear = Math.min(currentYear - 20, selectedYear)
    const maxYear = Math.max(currentYear + 5, selectedYear)

    return __range(minYear, maxYear + 1, 1).map(value => ({
      value,
      label: value.toString(),
    }))
  }, [selected])

  const controlledMinDate = React.useMemo(() => {
    const yearsMinDate = new Date(yearsOptions[0].value, 0, 1)

    if (!minDate) {
      return yearsMinDate
    }

    const providedMinDate = new Date(minDate)
    return providedMinDate > yearsMinDate ? providedMinDate : yearsMinDate
  }, [minDate, yearsOptions])

  const controlledMaxDate = React.useMemo(() => {
    const yearsMaxDate = new Date(yearsOptions[yearsOptions.length - 1].value, 11, 31)

    if (!maxDate) {
      return yearsMaxDate
    }

    const providedMaxDate = new Date(maxDate)
    return providedMaxDate < yearsMaxDate ? providedMaxDate : yearsMaxDate
  }, [maxDate, yearsOptions])

  const months = React.useMemo(
    () =>
      __range(0, 12, 1).map(value => ({
        value,
        label: format(new Date(1900, value, 6), 'MMMM', {
          ...(locale === 'hu' && { locale: dateLocaleHu }),
        }),
      })),
    [locale]
  )

  return (
    <DatePicker
      {...props}
      dateFormatCalendar={CALENDAR_LOCALE_FORMATS.header[locale as Locale]}
      locale={locale}
      maxDate={controlledMaxDate}
      minDate={controlledMinDate}
      selected={selected}
      wrapperClassName={className}
      renderCustomHeader={({
        date,
        changeYear,
        changeMonth,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
      }) => (
        <CustomHeader>
          <IconButton size="small" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
            <KeyboardArrowLeftIcon size={20} />
          </IconButton>
          <DatePickerDropdown
            options={yearsOptions}
            value={getYear(date)}
            onChange={changeYear}
            style={{ order: locale === 'hu' ? 2 : 3 }}
          />
          {!hideMonthSelect && (
            <StyledDatePickerDropdown
              options={months}
              value={getMonth(date)}
              onChange={changeMonth}
              style={{ order: locale === 'hu' ? 3 : 2 }}
            />
          )}
          <IconButton size="small" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
            <KeyboardArrowRightIcon size={20} />
          </IconButton>
        </CustomHeader>
      )}
    />
  )
}
